import { Box, IconButton, Switch, Typography, FormControlLabel } from "@mui/material";
import { ArrowForward } from "@mui/icons-material";
import { ReactComponent as DeleteIcon } from "../assets/images/delete.svg";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  getFoodDictionaryById,
  updateFoodDictionaryCertified,
  deleteFoodDictionary,
  getFoodDictionaryUsage,
} from "../services/system/FoodDictionaryService";
import {
  getFoodById as getCompanyFoodById,
  getFoodUsage,
  deleteFood as deleteCompanyFood,
} from "../services/FoodDictionaryService";
import ConfirmDialog from "../componenets/ui/ConfirmDialog";
import FoodDictionaryProfileSection from "../componenets/foodDictionary/FoodDictionaryProfileSection";
import FoodDictionaryNutrientTable from "../componenets/foodDictionary/FoodDictionaryNutrientTable";
import FoodDictionaryServingsSection from "../componenets/foodDictionary/FoodDictionaryServingsSection";
import BrandButton from "../componenets/foodDictionary/BrandButton";
import { useMessageContext } from "../contexts/MessageContext";
import { useAuth } from "../contexts/AuthContext";
import { getErrorMessage } from "../enums/ErrorMessages";
import { t } from "../componenets/dashboard/coach/coachTokens";
import { resolveNutritionBasis } from "../componenets/foodDictionary/foodDictionaryNutrientUtils";

const FoodDictionaryPage = () => {
  const { foodId } = useParams();
  const navigate = useNavigate();
  const { notificationHandler } = useMessageContext();
  const { isSuperAdmin } = useAuth();
  const [food, setFood] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null); // usage counts awaiting confirmation

  // Which portion the nutrition panel is reading from. Starts on the panel's own default basis so the
  // two halves of the screen agree before anyone clicks, and follows the selection after that.
  const [selectedServingId, setSelectedServingId] = useState(null);

  // A system food belongs to the platform, so only a system admin may touch it. Everything else the
  // company endpoint returns is this company's own — it never hands back another company's food.
  const canEdit = isSuperAdmin() || food?.scope === "COMPANY";

  // A system admin reads through the unscoped endpoint (it can reach any food, including a trainee's);
  // everyone else through the company-scoped one, which 404s on anything outside their company.
  const loadFood = () => {
    const read = isSuperAdmin() ? getFoodDictionaryById : getCompanyFoodById;
    read(foodId)
      .then((r) => {
        setFood(r.data);
        // Re-point at the default basis on every load. A reload after editing portions can leave the
        // selected id pointing at a portion that no longer exists, and an empty table is a worse
        // answer than the food's headline numbers.
        setSelectedServingId(resolveNutritionBasis(r.data).serving?.id ?? null);
      })
      .catch((e) => notificationHandler.error(getErrorMessage(e.response?.data?.errorCode)));
  };

  useEffect(() => {
    if (foodId) loadFood();
  }, [foodId, isSuperAdmin]);

  const handleCertifiedToggle = async (_, checked) => {
    try {
      const r = await updateFoodDictionaryCertified(foodId, checked);
      setFood(r.data);
      notificationHandler.success(checked ? "המזון אושר" : "אישור הוסר");
    } catch (e) {
      notificationHandler.error(getErrorMessage(e.response?.data?.errorCode));
    }
  };

  /**
   * Deleting a food removes it from every menu that plans it, so the counts come first.
   *
   * <p>A food nothing uses is deleted straight away — a dialog reading "0 menus" is noise. Otherwise
   * the numbers are shown and nothing happens until the person agrees.
   */
  const handleDelete = async () => {
    try {
      const readUsage = isSuperAdmin() ? getFoodDictionaryUsage : getFoodUsage;
      const { data: usage } = await readUsage(foodId);
      const inUse =
        usage.traineeMenuItems > 0 || usage.globalTemplateItems > 0 || usage.alternatives > 0;
      if (inUse) {
        setPendingDelete(usage);
        return;
      }
      await confirmDelete();
    } catch (e) {
      notificationHandler.error(getErrorMessage(e.response?.data?.errorCode));
    }
  };

  const confirmDelete = async () => {
    const remove = isSuperAdmin()
      ? () => deleteFoodDictionary(foodId, { confirm: true })
      : () => deleteCompanyFood(foodId, { confirm: true });
    try {
      await remove();
      notificationHandler.success("נמחק בהצלחה");
      navigate("/app/food-dictionary");
    } catch (e) {
      notificationHandler.error(getErrorMessage(e.response?.data?.errorCode));
    } finally {
      setPendingDelete(null);
    }
  };

  return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: "18px", width: "100%", direction: "rtl" }}>
        {/* Header: back + title + delete */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <IconButton
            onClick={() => navigate("/app/food-dictionary")}
            sx={{ color: t.t2, border: `1px solid ${t.border}`, background: t.white, borderRadius: t.rs,
              width: 38, height: 38, "&:hover": { borderColor: t.green, color: t.green, background: t.greenLight } }}
          >
            <ArrowForward />
          </IconButton>
          <Typography sx={{ fontSize: { xs: "20px", md: "24px" }, fontWeight: 800, color: t.text }}>
            פרטי מזון
          </Typography>
        </Box>

        {food && (
          <>
            {/* Action toolbar. Rendered only when there is something here the viewer may actually do
                — for a company admin looking at a system food that is nothing, and an empty bar reads
                better than a row of controls that answer 403. */}
            {(isSuperAdmin() || canEdit) && (
              <Box sx={{
                display: "flex", flexWrap: "wrap", gap: 1.25, alignItems: "center",
                p: 1.5, borderRadius: t.r, background: t.white, border: `1px solid ${t.border}`, boxShadow: t.sh,
              }}>
                {/* Certification is platform curation: it says "we vouch for this entry" to every
                    company at once, and it is a /system route. Never a company action. */}
                {isSuperAdmin() && (
                  <FormControlLabel
                    sx={{ ml: 0, mr: 0, "& .MuiFormControlLabel-label": { fontSize: "13px", color: t.t2 } }}
                    control={<Switch checked={!!food.certified} onChange={handleCertifiedToggle} color="success" />}
                    label="מזון מאושר"
                  />
                )}

                {canEdit && (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25, alignItems: "center", ml: "auto" }}>
                    <BrandButton variant="secondary" onClick={handleDelete}
                      icon={<DeleteIcon style={{ width: 16, height: 16 }} />}
                      sx={{ color: t.redText, "&:hover": { borderColor: t.red, color: t.red, background: t.redLight } }}>
                      מחק
                    </BrandButton>
                  </Box>
                )}
              </Box>
            )}

            {/* Profile header card (image + name/brand + status chips + edit) */}
            <FoodDictionaryProfileSection food={food} canEdit={canEdit} onUpdate={loadFood} />

            {/* Half-and-half: nutrient table on one side, serving sizes on the other */}
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: "18px", alignItems: "start" }}>
              <FoodDictionaryNutrientTable food={food} servingId={selectedServingId} />
              <FoodDictionaryServingsSection
                food={food}
                canEdit={canEdit}
                onUpdate={loadFood}
                selectedServingId={selectedServingId}
                onSelectServing={(s) => setSelectedServingId(s.id)}
              />
            </Box>
          </>
        )}

        {/* What deleting actually does, before it happens. Menus lose the food; logged meals do not
            change, and saying so matters — "12 menus" alone reads like history is at risk too. */}
        <ConfirmDialog
          open={Boolean(pendingDelete)}
          onClose={() => setPendingDelete(null)}
          onConfirm={confirmDelete}
          title="למחוק את המזון?"
          description={pendingDelete ? deletionSummary(pendingDelete) : ""}
          confirmText="מחק והסר מהתפריטים"
          variant="danger"
        />
      </Box>
  );
};

/** Plain Hebrew for the counts — only the parts that are non-zero, so nothing reads "0 תפריטים". */
const deletionSummary = (usage) => {
  const parts = [];
  if (usage.traineeMenus > 0) {
    parts.push(
      `${usage.traineeMenus} תפריטים` +
        (usage.affectedTrainees > 0 ? ` של ${usage.affectedTrainees} מתאמנים` : "")
    );
  }
  if (usage.globalTemplates > 0) parts.push(`${usage.globalTemplates} תבניות תפריט`);
  if (usage.alternatives > 0) parts.push(`${usage.alternatives} חלופות`);

  const used = parts.length ? `המזון בשימוש ב-${parts.join(", ")}. מחיקה תסיר אותו מכולם.` : "";
  const logged =
    usage.loggedEntries > 0
      ? ` ${usage.loggedEntries} רישומי יומן שכבר בוצעו יישארו ללא שינוי.`
      : "";
  return used + logged;
};

export default FoodDictionaryPage;
